import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  matchTextFilter,
  matchAmountFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { formatCurrency } from './reportData';
import { fetchAdminReports } from '../../../services/admin/adminReportsData';
import { invalidateCache } from '../../../services/cache/queryCache';
import { PAYMENTS_UPDATE_EVENT } from '../../receptionist/paymentStore';
import { isReceptionistPanelScope } from '../../../services/gym/branchContext';
import { pkTodayRange } from '../../../utils/pkTime';
import { useAuth } from '../../../context/AuthContext';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import {
  exportCsv,
  exportExcel,
  exportPdf,
  printReports,
  exportReportSection,
} from './exportReports';
import '../../owner/ownerPages.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './reports.css';

const emptyReports = {
  memberReport: [],
  attendanceReport: [],
  revenueReport: [],
  membershipReport: [],
  trainerReport: [],
  profitReport: [],
  branchReport: [],
  memberOptions: [],
};

function EmptyRow({ colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '20px' }}>
        No data available
      </td>
    </tr>
  );
}

function ReportManagement() {
  const { account } = useAuth();
  const gymId = account?.gym_id || '';
  const receptionistScope = isReceptionistPanelScope();
  const todayRange = pkTodayRange();
  const [reports, setReports] = useState(emptyReports);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const listRangeStart = receptionistScope ? todayRange.startDate : rangeStart;
  const listRangeEnd = receptionistScope ? todayRange.endDate : rangeEnd;

  const {
    filters: memberFilters,
    updateFilter: updateMemberFilter,
    openFilterKey: memberOpenKey,
    toggleFilter: toggleMemberFilter,
  } = useColumnFilters({
    status: '',
    count: '',
    newThisMonth: '',
    churned: '',
  });

  const {
    filters: attendanceFilters,
    updateFilter: updateAttendanceFilter,
    openFilterKey: attendanceOpenKey,
    toggleFilter: toggleAttendanceFilter,
  } = useColumnFilters({
    branch: '',
    checkIns: '',
    avgDaily: '',
    percentage: '',
  });

  const {
    filters: revenueFilters,
    updateFilter: updateRevenueFilter,
    openFilterKey: revenueOpenKey,
    toggleFilter: toggleRevenueFilter,
  } = useColumnFilters({
    month: '',
    memberships: '',
    other: '',
    total: '',
  });

  const {
    filters: membershipFilters,
    updateFilter: updateMembershipFilter,
    openFilterKey: membershipOpenKey,
    toggleFilter: toggleMembershipFilter,
  } = useColumnFilters({
    package: '',
    active: '',
    renewals: '',
    revenue: '',
  });

  const {
    filters: trainerFilters,
    updateFilter: updateTrainerFilter,
    openFilterKey: trainerOpenKey,
    toggleFilter: toggleTrainerFilter,
  } = useColumnFilters({
    trainer: '',
    members: '',
    sessions: '',
    rating: '',
  });

  const {
    filters: profitFilters,
    updateFilter: updateProfitFilter,
    openFilterKey: profitOpenKey,
    toggleFilter: toggleProfitFilter,
  } = useColumnFilters({
    month: '',
    revenue: '',
    expenses: '',
    salaries: '',
    profit: '',
  });

  const {
    filters: branchFilters,
    updateFilter: updateBranchFilter,
    openFilterKey: branchOpenKey,
    toggleFilter: toggleBranchFilter,
  } = useColumnFilters({
    branch: '',
    members: '',
    revenue: '',
    attendance: '',
    trainers: '',
  });

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setReports(await fetchAdminReports({
        dateFrom: listRangeStart,
        dateTo: listRangeEnd,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load reports');
      setReports(emptyReports);
    } finally {
      setLoading(false);
    }
  }, [listRangeStart, listRangeEnd]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadReports();
    }, listRangeStart || listRangeEnd ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadReports, listRangeStart, listRangeEnd]);

  useEffect(() => {
    const refresh = () => {
      if (gymId) invalidateCache(`gym:${gymId}:admin-reports:v3`);
      loadReports();
    };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [gymId, loadReports]);

  const {
    memberReport,
    attendanceReport,
    revenueReport,
    membershipReport,
    trainerReport,
    profitReport,
    branchReport,
  } = reports;

  const filteredMembers = useMemo(
    () => memberReport.filter((row) => (
      matchTextFilter(row.status, memberFilters.status)
      && matchTextFilter(row.count, memberFilters.count)
      && matchTextFilter(row.newThisMonth, memberFilters.newThisMonth)
      && matchTextFilter(row.churned, memberFilters.churned)
    )),
    [memberReport, memberFilters],
  );

  const filteredAttendance = useMemo(
    () => attendanceReport.filter((row) => (
      matchTextFilter(row.branch, attendanceFilters.branch)
      && matchTextFilter(String(row.checkIns.toLocaleString()), attendanceFilters.checkIns)
      && matchTextFilter(row.avgDaily, attendanceFilters.avgDaily)
      && matchTextFilter(`${row.percentage}%`, attendanceFilters.percentage)
    )),
    [attendanceReport, attendanceFilters],
  );

  const filteredRevenue = useMemo(
    () => revenueReport.filter((row) => (
      matchTextFilter(row.month, revenueFilters.month)
      && matchAmountFilter(row.memberships, revenueFilters.memberships, formatCurrency)
      && matchAmountFilter(row.other, revenueFilters.other, formatCurrency)
      && matchAmountFilter(row.total, revenueFilters.total, formatCurrency)
    )),
    [revenueReport, revenueFilters],
  );

  const filteredMemberships = useMemo(
    () => membershipReport.filter((row) => (
      matchTextFilter(row.package, membershipFilters.package)
      && matchTextFilter(row.active, membershipFilters.active)
      && matchTextFilter(row.renewals, membershipFilters.renewals)
      && matchAmountFilter(row.revenue, membershipFilters.revenue, formatCurrency)
    )),
    [membershipReport, membershipFilters],
  );

  const filteredTrainers = useMemo(
    () => trainerReport.filter((row) => (
      matchTextFilter(row.trainer, trainerFilters.trainer)
      && matchTextFilter(row.members, trainerFilters.members)
      && matchTextFilter(row.sessions, trainerFilters.sessions)
      && matchTextFilter(row.rating, trainerFilters.rating)
    )),
    [trainerReport, trainerFilters],
  );

  const filteredProfit = useMemo(
    () => profitReport.filter((row) => (
      matchTextFilter(row.month, profitFilters.month)
      && matchAmountFilter(row.revenue, profitFilters.revenue, formatCurrency)
      && matchAmountFilter(row.expenses, profitFilters.expenses, formatCurrency)
      && matchAmountFilter(row.salaries, profitFilters.salaries, formatCurrency)
      && matchAmountFilter(row.profit, profitFilters.profit, formatCurrency)
    )),
    [profitReport, profitFilters],
  );

  const filteredBranches = useMemo(
    () => branchReport.filter((row) => (
      matchTextFilter(row.branch, branchFilters.branch)
      && matchTextFilter(row.members, branchFilters.members)
      && matchAmountFilter(row.revenue, branchFilters.revenue, formatCurrency)
      && matchTextFilter(`${row.attendance}%`, branchFilters.attendance)
      && matchTextFilter(row.trainers, branchFilters.trainers)
    )),
    [branchReport, branchFilters],
  );

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="View and export gym performance reports"
      />

      <PrintActionsHeader
        startDate={listRangeStart}
        endDate={listRangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={receptionistScope ? undefined : () => { setRangeStart(''); setRangeEnd(''); }}
        datesReadOnly={receptionistScope}
        onPrint={() => printReports(reports)}
        onCsv={() => exportCsv(reports)}
        onPdf={() => exportPdf(reports)}
        onExcel={() => exportExcel(reports)}
        ariaLabel="Reports print and export"
      />
      {receptionistScope ? (
        <p className="owner-placeholder-text owner-mb-16">
          Pakistan time · today only — payments and attendance for today
        </p>
      ) : null}

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading reports...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}

      <div className="owner-card rec-panel-card admin-report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Member Report</h2>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportSection('Member Report', ['Status', 'Count', 'New This Month', 'Churned'], filteredMembers.map((r) => [r.status, r.count, r.newThisMonth, r.churned]), 'member-report')}>
            Download
          </button>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-report-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={memberOpenKey}
                  onToggle={toggleMemberFilter}
                  filters={memberFilters}
                  onFilterChange={updateMemberFilter}
                  placeholder="Search status..."
                />
                <ColumnFilterHeader
                  label="Count"
                  filterKey="count"
                  openKey={memberOpenKey}
                  onToggle={toggleMemberFilter}
                  filters={memberFilters}
                  onFilterChange={updateMemberFilter}
                  placeholder="Search count..."
                />
                <ColumnFilterHeader
                  label="New This Month"
                  filterKey="newThisMonth"
                  openKey={memberOpenKey}
                  onToggle={toggleMemberFilter}
                  filters={memberFilters}
                  onFilterChange={updateMemberFilter}
                  placeholder="Search new..."
                />
                <ColumnFilterHeader
                  label="Churned"
                  filterKey="churned"
                  openKey={memberOpenKey}
                  onToggle={toggleMemberFilter}
                  filters={memberFilters}
                  onFilterChange={updateMemberFilter}
                  placeholder="Search churned..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length > 0 ? filteredMembers.map((row) => (
                <tr key={row.status}>
                  <td>{row.status}</td><td>{row.count}</td><td>{row.newThisMonth}</td><td>{row.churned}</td>
                </tr>
              )) : <EmptyRow colSpan={4} />}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Attendance Report</h2>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportSection('Attendance Report', ['Branch', 'Check-ins', 'Avg Daily', 'Percentage'], filteredAttendance.map((r) => [r.branch, r.checkIns, r.avgDaily, `${r.percentage}%`]), 'attendance-report')}>
            Download
          </button>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-report-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={attendanceOpenKey}
                  onToggle={toggleAttendanceFilter}
                  filters={attendanceFilters}
                  onFilterChange={updateAttendanceFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Check-ins"
                  filterKey="checkIns"
                  openKey={attendanceOpenKey}
                  onToggle={toggleAttendanceFilter}
                  filters={attendanceFilters}
                  onFilterChange={updateAttendanceFilter}
                  placeholder="Search check-ins..."
                />
                <ColumnFilterHeader
                  label="Avg Daily"
                  filterKey="avgDaily"
                  openKey={attendanceOpenKey}
                  onToggle={toggleAttendanceFilter}
                  filters={attendanceFilters}
                  onFilterChange={updateAttendanceFilter}
                  placeholder="Search avg daily..."
                />
                <ColumnFilterHeader
                  label="Attendance %"
                  filterKey="percentage"
                  openKey={attendanceOpenKey}
                  onToggle={toggleAttendanceFilter}
                  filters={attendanceFilters}
                  onFilterChange={updateAttendanceFilter}
                  placeholder="Search %..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredAttendance.length > 0 ? filteredAttendance.map((row) => (
                <tr key={row.branch}>
                  <td>{row.branch}</td><td>{row.checkIns.toLocaleString()}</td><td>{row.avgDaily}</td><td>{row.percentage}%</td>
                </tr>
              )) : <EmptyRow colSpan={4} />}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Revenue Report</h2>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportSection('Revenue Report', ['Month', 'Memberships', 'Other', 'Total'], filteredRevenue.map((r) => [r.month, r.memberships, r.other, r.total]), 'revenue-report')}>
            Download
          </button>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-report-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Month"
                  filterKey="month"
                  openKey={revenueOpenKey}
                  onToggle={toggleRevenueFilter}
                  filters={revenueFilters}
                  onFilterChange={updateRevenueFilter}
                  placeholder="Search month..."
                />
                <ColumnFilterHeader
                  label="Memberships"
                  filterKey="memberships"
                  openKey={revenueOpenKey}
                  onToggle={toggleRevenueFilter}
                  filters={revenueFilters}
                  onFilterChange={updateRevenueFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Other"
                  filterKey="other"
                  openKey={revenueOpenKey}
                  onToggle={toggleRevenueFilter}
                  filters={revenueFilters}
                  onFilterChange={updateRevenueFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Total"
                  filterKey="total"
                  openKey={revenueOpenKey}
                  onToggle={toggleRevenueFilter}
                  filters={revenueFilters}
                  onFilterChange={updateRevenueFilter}
                  placeholder="Search amount..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredRevenue.length > 0 ? filteredRevenue.map((row) => (
                <tr key={row.month}>
                  <td>{row.month}</td>
                  <td className="report-amount">{formatCurrency(row.memberships)}</td>
                  <td>{formatCurrency(row.other)}</td>
                  <td className="report-amount">{formatCurrency(row.total)}</td>
                </tr>
              )) : <EmptyRow colSpan={4} />}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Membership Report</h2>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportSection('Membership Report', ['Package', 'Active', 'Renewals', 'Revenue'], filteredMemberships.map((r) => [r.package, r.active, r.renewals, r.revenue]), 'membership-report')}>
            Download
          </button>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-report-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Package"
                  filterKey="package"
                  openKey={membershipOpenKey}
                  onToggle={toggleMembershipFilter}
                  filters={membershipFilters}
                  onFilterChange={updateMembershipFilter}
                  placeholder="Search package..."
                />
                <ColumnFilterHeader
                  label="Active"
                  filterKey="active"
                  openKey={membershipOpenKey}
                  onToggle={toggleMembershipFilter}
                  filters={membershipFilters}
                  onFilterChange={updateMembershipFilter}
                  placeholder="Search active..."
                />
                <ColumnFilterHeader
                  label="Renewals"
                  filterKey="renewals"
                  openKey={membershipOpenKey}
                  onToggle={toggleMembershipFilter}
                  filters={membershipFilters}
                  onFilterChange={updateMembershipFilter}
                  placeholder="Search renewals..."
                />
                <ColumnFilterHeader
                  label="Revenue"
                  filterKey="revenue"
                  openKey={membershipOpenKey}
                  onToggle={toggleMembershipFilter}
                  filters={membershipFilters}
                  onFilterChange={updateMembershipFilter}
                  placeholder="Search amount..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredMemberships.length > 0 ? filteredMemberships.map((row) => (
                <tr key={row.package}>
                  <td>{row.package}</td><td>{row.active}</td><td>{row.renewals}</td>
                  <td className="report-amount">{formatCurrency(row.revenue)}</td>
                </tr>
              )) : <EmptyRow colSpan={4} />}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Trainer Report</h2>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportSection('Trainer Report', ['Trainer', 'Members', 'Sessions', 'Rating'], filteredTrainers.map((r) => [r.trainer, r.members, r.sessions, r.rating]), 'trainer-report')}>
            Download
          </button>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-report-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="trainer"
                  openKey={trainerOpenKey}
                  onToggle={toggleTrainerFilter}
                  filters={trainerFilters}
                  onFilterChange={updateTrainerFilter}
                  placeholder="Search trainer..."
                />
                <ColumnFilterHeader
                  label="Assigned Members"
                  filterKey="members"
                  openKey={trainerOpenKey}
                  onToggle={toggleTrainerFilter}
                  filters={trainerFilters}
                  onFilterChange={updateTrainerFilter}
                  placeholder="Search members..."
                />
                <ColumnFilterHeader
                  label="Sessions"
                  filterKey="sessions"
                  openKey={trainerOpenKey}
                  onToggle={toggleTrainerFilter}
                  filters={trainerFilters}
                  onFilterChange={updateTrainerFilter}
                  placeholder="Search sessions..."
                />
                <ColumnFilterHeader
                  label="Rating"
                  filterKey="rating"
                  openKey={trainerOpenKey}
                  onToggle={toggleTrainerFilter}
                  filters={trainerFilters}
                  onFilterChange={updateTrainerFilter}
                  placeholder="Search rating..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredTrainers.length > 0 ? filteredTrainers.map((row) => (
                <tr key={row.trainer}>
                  <td>{row.trainer}</td><td>{row.members}</td><td>{row.sessions}</td><td>{row.rating}</td>
                </tr>
              )) : <EmptyRow colSpan={4} />}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Profit Report</h2>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportSection('Profit Report', ['Month', 'Revenue', 'Expenses', 'Salaries', 'Profit'], filteredProfit.map((r) => [r.month, r.revenue, r.expenses, r.salaries, r.profit]), 'profit-report')}>
            Download
          </button>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-report-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Month"
                  filterKey="month"
                  openKey={profitOpenKey}
                  onToggle={toggleProfitFilter}
                  filters={profitFilters}
                  onFilterChange={updateProfitFilter}
                  placeholder="Search month..."
                />
                <ColumnFilterHeader
                  label="Revenue"
                  filterKey="revenue"
                  openKey={profitOpenKey}
                  onToggle={toggleProfitFilter}
                  filters={profitFilters}
                  onFilterChange={updateProfitFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Expenses"
                  filterKey="expenses"
                  openKey={profitOpenKey}
                  onToggle={toggleProfitFilter}
                  filters={profitFilters}
                  onFilterChange={updateProfitFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Salaries"
                  filterKey="salaries"
                  openKey={profitOpenKey}
                  onToggle={toggleProfitFilter}
                  filters={profitFilters}
                  onFilterChange={updateProfitFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Profit"
                  filterKey="profit"
                  openKey={profitOpenKey}
                  onToggle={toggleProfitFilter}
                  filters={profitFilters}
                  onFilterChange={updateProfitFilter}
                  placeholder="Search amount..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredProfit.length > 0 ? filteredProfit.map((row) => (
                <tr key={row.month}>
                  <td>{row.month}</td>
                  <td className="report-amount">{formatCurrency(row.revenue)}</td>
                  <td>{formatCurrency(row.expenses)}</td>
                  <td>{formatCurrency(row.salaries)}</td>
                  <td className="report-amount">{formatCurrency(row.profit)}</td>
                </tr>
              )) : <EmptyRow colSpan={5} />}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Branch Report</h2>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportSection('Branch Report', ['Branch', 'Members', 'Revenue', 'Attendance %', 'Trainers'], filteredBranches.map((r) => [r.branch, r.members, r.revenue, `${r.attendance}%`, r.trainers]), 'branch-report')}>
            Download
          </button>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-report-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={branchOpenKey}
                  onToggle={toggleBranchFilter}
                  filters={branchFilters}
                  onFilterChange={updateBranchFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Members"
                  filterKey="members"
                  openKey={branchOpenKey}
                  onToggle={toggleBranchFilter}
                  filters={branchFilters}
                  onFilterChange={updateBranchFilter}
                  placeholder="Search members..."
                />
                <ColumnFilterHeader
                  label="Revenue"
                  filterKey="revenue"
                  openKey={branchOpenKey}
                  onToggle={toggleBranchFilter}
                  filters={branchFilters}
                  onFilterChange={updateBranchFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Attendance %"
                  filterKey="attendance"
                  openKey={branchOpenKey}
                  onToggle={toggleBranchFilter}
                  filters={branchFilters}
                  onFilterChange={updateBranchFilter}
                  placeholder="Search attendance..."
                />
                <ColumnFilterHeader
                  label="Trainers"
                  filterKey="trainers"
                  openKey={branchOpenKey}
                  onToggle={toggleBranchFilter}
                  filters={branchFilters}
                  onFilterChange={updateBranchFilter}
                  placeholder="Search trainers..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredBranches.length > 0 ? filteredBranches.map((row) => (
                <tr key={row.branch}>
                  <td>{row.branch}</td><td>{row.members}</td>
                  <td className="report-amount">{formatCurrency(row.revenue)}</td>
                  <td>{row.attendance}%</td><td>{row.trainers}</td>
                </tr>
              )) : <EmptyRow colSpan={5} />}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ReportManagement;
